import { Spinner, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { hydrateAnalyticsIdentity, resetAnalytics } from '../app/analytics.js';
import { ApiError } from '../lib/apiFetch.js';
import { exitImpersonation, fetchMe, logout, type Session } from './authClient.js';
import { LoginScreen } from './LoginScreen.js';
import * as sessionRefreshScheduler from './sessionRefreshScheduler.js';
import { clearTokens, getAccessTokenExpiresIn } from './tokenStore.js';
import { SessionContext, type SessionState, type SessionStatus } from './useSession.js';

/**
 * Cross-origin auth guard against `api.<env>.qelm.app`.
 *
 * On mount it resolves the session via `GET /api/v1/me` (credentialed). A 401
 * inside apiFetch triggers a single-flight refresh automatically; if that also
 * fails, `/me` stays 401 and we land in the `unauthenticated` state showing the
 * "session expired" copy + a sign-in affordance. The access token never touches
 * storage — it lives in the in-memory tokenStore (threat T-20-09).
 *
 * When unauthenticated the guard renders the branded single-step `LoginScreen`
 * (email + password against `POST /api/v1/auth/login`, no MFA for tenant users
 * this phase). On success the screen calls back into `resolveSession`, which
 * re-resolves `/api/v1/me` BEFORE the shell renders.
 *
 * ⚠ That extra round trip is load-bearing, do not remove it again. The login
 * response does NOT carry the whole session. Measured against api.dev.qelm.app on
 * 2026-09-07, `POST /api/v1/auth/login` answers with exactly
 * `{ok, accessToken, accessTokenExpiresIn, refreshToken, refreshTokenExpiresAt,
 * user, tenant}` — no `features`, no `analytics`, no `impersonation`, all three of
 * which `GET /api/v1/me` does return. Adopting the login payload therefore handed
 * the shell `features: {servicetitan:false, new_shell:false}` for EVERY tenant, so
 * the sidebar was built from flags the tenant does not actually have and only
 * corrected itself on a full page reload (which goes through `/me`). The same gap
 * left PostHog unidentified and hid the impersonation banner.
 *
 * The session then lives in this component's state for the life of the tab. A
 * Flagship flip written in another tab is invisible until `/me` is read again —
 * logging out was the only remount operators had. While authenticated we re-read
 * `/me` when this tab becomes visible again (or the window is focused / restored
 * from bfcache) and adopt the new `features` without tearing the shell down.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const queryClient = useQueryClient();
  const sessionEpoch = useRef(0);
  const refreshInFlight = useRef<Promise<void> | null>(null);

  /**
   * Ending a session drops everything it left behind. The QueryClient is created once in
   * main.tsx and outlives this guard, so anything still cached is handed to whoever signs
   * in next on the same tab — on a shared crew tablet that is the previous person's jobs,
   * invoices and timesheets, served with no network call to correct it.
   *
   * EVERY exit from `authenticated` goes through here: sign-out, exit-impersonation, a
   * resolve that came back 401, and the refresh scheduler giving up on an expired session.
   * The expiry path is the one that matters most — nobody clicks anything, so nothing else
   * would have cleared it.
   */
  const clearSession = useCallback(() => {
    sessionEpoch.current += 1;
    resetAnalytics();
    clearTokens();
    setSession(null);
    setStatus('unauthenticated');
    void queryClient.cancelQueries();
    // A mutation is not a query: clearing the query cache leaves the mutation cache holding
    // in-flight work whose success handler writes rows straight back in.
    queryClient.getMutationCache().clear();
    queryClient.clear();
  }, [queryClient]);

  /**
   * Clearing twice is not enough on its own. A mutation whose request had already resolved
   * runs its success handler on its own schedule — seconds later, after any "next tick"
   * clear has long since fired — and `setQueryData` happily recreates the row it was
   * holding. So while nobody is signed in, anything that appears in the cache is removed as
   * it appears, whatever put it there.
   *
   * ponytail: this closes the gap between sign-out and the next sign-in, which is the one
   * that matters — a shared tablet handed to the next person. A handler landing after that
   * second sign-in would still write; namespacing every query key by user id is the airtight
   * fix and it touches every feature directory.
   */
  useEffect(() => {
    if (status === 'authenticated') return;
    const cache = queryClient.getQueryCache();
    return cache.subscribe((event) => {
      if (event.type === 'added') cache.remove(event.query);
    });
  }, [status, queryClient]);

  /**
   * The ONE way a session is adopted — on a cold load and after a sign-in alike.
   * `/api/v1/me` is the only response that carries `features`, `analytics` and
   * `impersonation`, so the shell is never rendered from anything else.
   *
   * `silent` is the sign-in path: it skips the loading state so the login screen stays
   * mounted, which keeps the typed email if the resolve then fails, and rethrows so that
   * screen can say so in its own error region.
   */
  const resolveSession = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setStatus('loading');
      try {
        const next = await fetchMe();
        hydrateAnalyticsIdentity(next);
        setSession(next);
        setStatus('authenticated');
      } catch (err) {
        // 401 is the ONLY answer that means "not signed in". apiFetch already ran a
        // single-flight refresh and retried once before it reached here, so calling
        // refreshSession() again would re-run the request that just failed.
        if (err instanceof ApiError && err.status === 401) {
          clearSession();
          // On the sign-in path the login screen is still mounted with its button busy. A
          // quiet return leaves it busy for good; the throw lets it reset and say so.
          if (silent) throw err;
          return;
        }
        // Anything else — 403, 500, a dropped connection — says the session could not be
        // RESOLVED, not that it is invalid. Keep the tokens.
        if (silent) throw err;
        setStatus('unavailable');
      }
    },
    [clearSession],
  );

  useEffect(() => {
    void resolveSession();
  }, [resolveSession]);

  /**
   * Re-read `/me` without flipping `status` back to `loading`. That status
   * unmounts the signed-in tree and flashes the dark spinner — the opposite of
   * "they clicked back into the dashboard and the flag was on." A 401 still
   * ends the session; any other failure keeps the chrome they already have.
   * `sessionEpoch` drops a response that landed after sign-out started.
   */
  const refreshSessionFromMe = useCallback(() => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const epoch = sessionEpoch.current;
    const run = (async () => {
      try {
        const next = await fetchMe();
        if (epoch !== sessionEpoch.current) return;
        hydrateAnalyticsIdentity(next);
        setSession(next);
      } catch (err) {
        if (epoch !== sessionEpoch.current) return;
        if (err instanceof ApiError && err.status === 401) clearSession();
      }
    })();
    refreshInFlight.current = run;
    void run.finally(() => {
      if (refreshInFlight.current === run) refreshInFlight.current = null;
    });
    return run;
  }, [clearSession]);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const onResume = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshSessionFromMe();
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [status, refreshSessionFromMe]);

  // Proactive pre-expiry silent refresh: while authenticated, keep the access
  // token (and the durable qelm_refresh cookie) rotated ~60s before the 15m
  // expiry so an in-use tenant is never bounced to the login screen (22-UAT
  // test 5). signOut / exitImpersonation / a failed refresh all transition
  // `status` away from 'authenticated', which stops the scheduler here; unmount
  // stops it via the cleanup. onExpired mirrors the reactive failure path so the
  // user sees the branded LoginScreen rather than a silent broken state.
  useEffect(() => {
    if (status !== 'authenticated') {
      sessionRefreshScheduler.stop();
      return;
    }
    sessionRefreshScheduler.start({
      getExpiresInSeconds: getAccessTokenExpiresIn,
      onExpired: clearSession,
    });
    return () => sessionRefreshScheduler.stop();
  }, [status, clearSession]);

  const signOut = useCallback(async () => {
    sessionEpoch.current += 1;
    await logout();
    clearSession();
  }, [clearSession]);

  const exitCurrentImpersonation = useCallback(async () => {
    sessionEpoch.current += 1;
    const result = await exitImpersonation();
    clearSession();
    window.location.assign(result.platformAdminUrl);
  }, [clearSession]);

  const value = useMemo<SessionState>(
    () => ({
      status,
      session,
      signOut,
      exitImpersonation: exitCurrentImpersonation,
      refreshSession: refreshSessionFromMe,
    }),
    [status, session, signOut, exitCurrentImpersonation, refreshSessionFromMe],
  );

  if (status === 'loading') {
    // Same `.dark fw-page` surface the LoginScreen and the go.<zone> finder use.
    // Until `/me` answers we do not know whether this person is signed in, and a
    // themed background here would flash the app's light surface between the
    // dark finder and the dark login card — the seam this whole surface exists
    // to remove. The signed-in tree below is untouched and keeps the user's own
    // appearance preference.
    return (
      <div className="dark fw-page">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <LoginScreen onSignedIn={() => resolveSession({ silent: true })} />;
  }

  if (status === 'unavailable') {
    // Same dark auth surface as the login screen, so a failure here does not throw the
    // person onto a differently-branded page. The tokens are untouched: Try again is a
    // second attempt at the same request, not a second sign-in.
    return (
      <main className="dark fw-page">
        <div className="fw-shell">
          <section className="fw-card">
            <p className="fw-error" role="alert" aria-live="assertive">
              {t('common:login.unavailableBody')}
            </p>
            <button type="button" className="fw-submit" onClick={() => void resolveSession()}>
              {t('common:login.retry')}
            </button>
          </section>
        </div>
      </main>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
